import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { api, ApiError } from "../lib/api"
import { useAuth, type PrincipalUser, type Membership } from "../lib/auth"

interface LoginResponse {
  token: string
  user: PrincipalUser
}

interface MeResponse {
  user: PrincipalUser
  memberships: Membership[]
}

export function LoginPage() {
  const navigate = useNavigate()
  const setSession = useAuth((s) => s.setSession)
  const setMemberships = useAuth((s) => s.setMemberships)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState("")
  const [submitting, setSubmitting] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError("")
    setSubmitting(true)
    try {
      const res = await api<LoginResponse>("/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), password }),
      })
      setSession(res.token, res.user)

      // Pull memberships immediately so we know which tenant to land in.
      const me = await api<MeResponse>("/v1/auth/me")
      setMemberships(me.memberships)

      const firstSlug = me.memberships[0]?.tenantSlug
      if (firstSlug) {
        navigate(`/t/${firstSlug}/employees`, { replace: true })
      } else if (res.user.isPlatformAdmin) {
        navigate("/admin", { replace: true })
      } else {
        setError("No tenant access yet. Ask an admin to add you to a tenant.")
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Invalid email or password.")
      } else {
        setError(err instanceof Error ? err.message : "Login failed")
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-sm bg-white border border-slate-200 rounded-lg p-6 shadow-sm"
      >
        <div className="mb-6">
          <div className="text-lg font-semibold text-brand-700">ClearAI People</div>
          <div className="text-xs text-slate-400 mt-1">Sign in to continue</div>
        </div>

        <label className="block text-xs font-medium text-slate-600 mb-1">Username or email</label>
        <input
          type="text" autoComplete="username"
          autoFocus
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mb-4 px-3 py-2 border border-slate-300 rounded text-sm focus:border-brand-500 focus:outline-none"
        />

        <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
        <input
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full mb-4 px-3 py-2 border border-slate-300 rounded text-sm focus:border-brand-500 focus:outline-none"
        />

        {error && <div className="mb-4 text-sm text-red-600">{error}</div>}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="w-full py-2 bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 text-white text-sm font-medium rounded"
        >
          {submitting ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  )
}
